import { useMemo, useState } from "react";
import PropTypes from "prop-types";
import { Tabs, Tab, IconButton, Icon, CircularProgress } from "@mui/material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import { useICCCColors, ICCC_COLORS as ICCC_FALLBACK } from "./theme";
import { BOTTOM_ROW_HEIGHT, SEVERITY_LABEL, severityToHealth } from "../model/constants";
import { useGetAlertHistoryForAssetQuery } from "features/icccApiSlice";

const ALERTS_RENDER_CAP = 200;

const fmtTime = (ms) => {
  if (!ms) return "—";
  const d = new Date(Number(ms));
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const SeverityChip = ({ severity }) => {
  const color = ICCC_FALLBACK[severityToHealth(severity)] || ICCC_FALLBACK.unknown;
  return (
    <span
      style={{
        background: color,
        color: "#fff",
        padding: "1px 6px",
        borderRadius: 3,
        fontSize: 10,
        fontWeight: 600,
        whiteSpace: "nowrap",
      }}
    >
      {SEVERITY_LABEL[Number(severity)] || "Unknown"}
    </span>
  );
};

SeverityChip.propTypes = { severity: PropTypes.number };

export default function AlertDrillDown({ selectedGroup, activeAlerts = [], onClear }) {
  const ICCC_COLORS = useICCCColors();
  const [tab, setTab] = useState(0);
  const [selectedAsset, setSelectedAsset] = useState(null);

  const th = {
    textAlign: "left",
    padding: "5px 8px",
    borderBottom: `1px solid ${ICCC_COLORS.divider}`,
    fontWeight: 600,
    color: ICCC_COLORS.textSecondary,
    fontSize: 11,
  };
  const td = {
    padding: "5px 8px",
    borderBottom: `1px solid ${ICCC_COLORS.divider}`,
    color: ICCC_COLORS.textSecondary,
    verticalAlign: "top",
  };

  const filtered = useMemo(() => {
    if (!selectedGroup) return activeAlerts;
    const want = selectedGroup.toUpperCase();
    return activeAlerts.filter((a) => a.category === want);
  }, [activeAlerts, selectedGroup]);

  const affectedDevices = useMemo(() => {
    const m = new Map();
    filtered.forEach((a) => {
      const entry = m.get(a.assetId) || {
        assetId: a.assetId,
        name: a.nodeName || `#${a.assetId}`,
        count: 0,
        maxSeverity: 0,
      };
      entry.count += 1;
      entry.maxSeverity = Math.max(entry.maxSeverity, a.severity);
      m.set(a.assetId, entry);
    });
    return Array.from(m.values()).sort((a, b) => b.maxSeverity - a.maxSeverity);
  }, [filtered]);

  const historyQ = useGetAlertHistoryForAssetQuery(selectedAsset, { skip: !selectedAsset });

  const openHistory = (assetId) => {
    setSelectedAsset(assetId);
    setTab(2);
  };

  const visibleAlerts = filtered.slice(0, ALERTS_RENDER_CAP);

  return (
    <MDBox
      sx={{
        bgcolor: ICCC_COLORS.cardBg,
        border: `1px solid ${ICCC_COLORS.cardBorder}`,
        borderRadius: 1.5,
        p: 2,
        height: BOTTOM_ROW_HEIGHT,
        display: "flex",
        flexDirection: "column",
        boxShadow: ICCC_COLORS.shadow,
      }}
    >
      <MDBox display="flex" justifyContent="space-between" alignItems="center">
        <MDTypography variant="h6" fontWeight="bold" sx={{ color: ICCC_COLORS.textPrimary }}>
          Alert Drill-down
        </MDTypography>
        {selectedGroup && (
          <IconButton size="small" onClick={onClear} sx={{ color: ICCC_COLORS.textSecondary }} aria-label="Clear group filter">
            <Icon fontSize="small">close</Icon>
          </IconButton>
        )}
      </MDBox>

      <MDBox display="flex" alignItems="center" gap={1} mt={0.5}>
        <Icon sx={{ color: ICCC_COLORS.accent }}>filter_alt</Icon>
        <MDTypography variant="body2" fontWeight="medium" sx={{ color: ICCC_COLORS.accent }}>
          Selected: {selectedGroup || "All Groups"}
        </MDTypography>
      </MDBox>
      <MDTypography variant="caption" sx={{ color: ICCC_COLORS.textMuted, display: "block", mb: 1 }}>
        {filtered.length.toLocaleString("en-IN")} active alerts in this view
      </MDTypography>

      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        variant="fullWidth"
        sx={{
          minHeight: 36,
          "& .MuiTab-root": { minHeight: 36, textTransform: "none", fontSize: 12, color: ICCC_COLORS.textSecondary },
          "& .MuiTab-root.Mui-selected": { color: ICCC_COLORS.accent },
          "& .MuiTabs-indicator": { backgroundColor: ICCC_COLORS.accent },
        }}
      >
        <Tab label={`Alerts (${filtered.length})`} />
        <Tab label={`Devices (${affectedDevices.length})`} />
        <Tab label="History" />
      </Tabs>

      <MDBox
        sx={{
          flex: 1,
          overflow: "auto",
          mt: 1,
          border: `1px solid ${ICCC_COLORS.divider}`,
          borderRadius: 1,
          bgcolor: ICCC_COLORS.cardBg2,
        }}
      >
        {tab === 0 && (
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11 }}>
            <thead style={{ position: "sticky", top: 0, background: ICCC_COLORS.cardBg2, zIndex: 1 }}>
              <tr>
                <th style={th}>Severity</th>
                <th style={th}>Message</th>
                <th style={th}>Raised</th>
              </tr>
            </thead>
            <tbody>
              {visibleAlerts.length === 0 && (
                <tr>
                  <td colSpan={3} style={{ ...td, textAlign: "center", color: ICCC_COLORS.textMuted, padding: 20 }}>
                    No active alerts
                  </td>
                </tr>
              )}
              {visibleAlerts.map((a) => (
                <tr key={a.id}>
                  <td style={td}>
                    <SeverityChip severity={a.severity} />
                  </td>
                  <td style={td}>{a.message}</td>
                  <td style={td}>{fmtTime(a.raisedAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {tab === 1 && (
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11 }}>
            <thead style={{ position: "sticky", top: 0, background: ICCC_COLORS.cardBg2, zIndex: 1 }}>
              <tr>
                <th style={th}>Asset</th>
                <th style={th}>Worst</th>
                <th style={th}>#</th>
              </tr>
            </thead>
            <tbody>
              {affectedDevices.length === 0 && (
                <tr>
                  <td colSpan={3} style={{ ...td, textAlign: "center", color: ICCC_COLORS.textMuted, padding: 20 }}>
                    No affected devices
                  </td>
                </tr>
              )}
              {affectedDevices.map((d) => (
                <tr
                  key={d.assetId}
                  onClick={() => openHistory(d.assetId)}
                  style={{ cursor: "pointer" }}
                  title="View alert history"
                  onMouseEnter={(e) => (e.currentTarget.style.background = ICCC_COLORS.hoverBg)}
                  onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                >
                  <td style={td}>{d.name}</td>
                  <td style={td}>
                    <SeverityChip severity={d.maxSeverity} />
                  </td>
                  <td style={td}>{d.count}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {tab === 2 && (
          <MDBox sx={{ height: "100%" }}>
            {!selectedAsset ? (
              <MDBox sx={{ p: 2, color: ICCC_COLORS.textMuted, fontSize: 12 }}>
                Select a device in the <strong>Devices</strong> tab to load its alert history.
              </MDBox>
            ) : historyQ.isFetching ? (
              <MDBox sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: 120 }}>
                <CircularProgress size={24} sx={{ color: ICCC_COLORS.accent }} />
              </MDBox>
            ) : (
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11 }}>
                <thead style={{ position: "sticky", top: 0, background: ICCC_COLORS.cardBg2, zIndex: 1 }}>
                  <tr>
                    <th style={th}>Severity</th>
                    <th style={th}>Message</th>
                    <th style={th}>Raised</th>
                    <th style={th}>Resolved</th>
                  </tr>
                </thead>
                <tbody>
                  {(historyQ.data || []).length === 0 && (
                    <tr>
                      <td colSpan={4} style={{ ...td, textAlign: "center", color: ICCC_COLORS.textMuted, padding: 20 }}>
                        No history for asset #{selectedAsset}
                      </td>
                    </tr>
                  )}
                  {(historyQ.data || []).map((a) => (
                    <tr key={a.id}>
                      <td style={td}>
                        <SeverityChip severity={a.severity} />
                      </td>
                      <td style={td}>{a.message}</td>
                      <td style={td}>{fmtTime(a.raisedAt)}</td>
                      <td style={td}>{a.closedAt ? fmtTime(a.closedAt) : "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </MDBox>
        )}
      </MDBox>
    </MDBox>
  );
}

AlertDrillDown.propTypes = {
  selectedGroup: PropTypes.string,
  activeAlerts: PropTypes.arrayOf(PropTypes.object),
  onClear: PropTypes.func,
};
